import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  IContact,
  INotificationData,
  IResponse,
  ISettlementEmailsData,
  ITenantBasicData,
  ITenantPostalInfo,
} from '../core.interface';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { BrandingService } from './branding.service';

@Injectable({
  providedIn: 'root',
})
export class Settings {
  private http = inject(HttpClient);
  private $branding = inject(BrandingService);

  public tenant = signal<ITenantBasicData | null>(null);
  public tenantPostalInfo = signal<ITenantPostalInfo | null>(null);

  public contact = signal<IContact | null>(null);
  private contactBrandUuid = signal<string | null>(null);
  public settlementEmails = signal<string[]>([]);
  public hasElectronicSettlement = signal<boolean>(false);
  public hasMailboxDeliveryService = signal<boolean>(false);

  public phoneList = signal<{ id: number | null; phone: number }[]>([]);
  public receiveWhatsapp = signal<boolean>(false);

  public async fetchTenantMe(tenantId: string): Promise<void> {
    if (this.tenant()?.tenantEmail) return;

    try {
      const response = await firstValueFrom(
        this.http.get<IResponse<ITenantBasicData>>(
          `${environment.apiUrl}/tenant/${tenantId}/basic-data`,
          { observe: 'response' },
        ),
      );

      if (response.status === 200 && response.body?.data) {
        this.tenant.set(response.body.data);
      }
    } catch (error) {
      console.error('Fetch tenantMe error:', error);
    }
  }

  public async fetchTenantPostalInfo(tenantId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<IResponse<ITenantPostalInfo>>(
          `${environment.apiUrl}/tenant/${tenantId}/postal-info`,
          { observe: 'response' },
        ),
      );

      if (response.status !== 200) {
        throw new Error('Error fetching basic-data');
      }

      const tenantPI = response.body!.data;
      this.tenantPostalInfo.set(tenantPI);
    } catch (error) {
      console.error('Fetch tenantMe error:', error);
      throw error;
    }
  }

  public async fetchContact(): Promise<void> {
    const brandUuid = this.$branding.brandUuid()?.trim() ?? '';
    if (!brandUuid) return;
    if (this.contactBrandUuid() === brandUuid && this.contact()) return;

    try {
      const response = await firstValueFrom(
        this.http.get<IResponse<IContact>>(`${environment.apiUrl}/public/contact`, {
          observe: 'response',
        }),
      );
      if (response.body?.data) {
        this.contact.set(response.body.data);
        this.contactBrandUuid.set(brandUuid);
      }
    } catch (error) {
      console.error('Fetch Contact error:', error);
    }
  }

  public async fetchSettlementEmails(tenantId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<IResponse<ISettlementEmailsData>>(
          `${environment.apiUrl}/tenant/${tenantId}/settlements/emails`,
          { observe: 'response' },
        ),
      );

      const data = response.body?.data;
      this.settlementEmails.set(data?.emails ?? []);
      this.hasElectronicSettlement.set(data?.hasElectronicSettlement ?? false);
      this.hasMailboxDeliveryService.set(data?.hasMailboxDeliveryService ?? false);
    } catch (error) {
      console.error('Fetch settlement emails error:', error);
      this.settlementEmails.set([]);
      this.hasElectronicSettlement.set(false);
      this.hasMailboxDeliveryService.set(false);
    }
  }

  public async activateEmailDeliveryService(
    tenantId: string,
    payload: { emails: string[]; disableMailboxDeliveryService?: boolean },
  ): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/tenant/${tenantId}/settlements/email-delivery-service/activate`,
          payload,
          { observe: 'response' },
        ),
      );

      return response.status === 200 || response.status === 201 || response.status === 204;
    } catch (error) {
      console.error('Activate email delivery service error:', error);
      return false;
    }
  }

  public async deactivateEmailDeliveryService(tenantId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/tenant/${tenantId}/settlements/email-delivery-service/deactivate`,
          {},
          { observe: 'response' },
        ),
      );

      return response.status === 200 || response.status === 201 || response.status === 204;
    } catch (error) {
      console.error('Deactivate email delivery service error:', error);
      return false;
    }
  }

  public async deactivateMailboxDeliveryService(tenantId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/tenant/${tenantId}/settlements/mailbox-delivery-service/deactivate`,
          {},
          { observe: 'response' },
        ),
      );

      return response.status === 200 || response.status === 201 || response.status === 204;
    } catch (error) {
      console.error('Deactivate mailbox delivery service error:', error);
      return false;
    }
  }

  public async sendNotification(data: INotificationData, tenantId: string): Promise<boolean> {
    try {
      const formData = new FormData();

      formData.append('contactInfo', data.contactInfo);
      formData.append('reason', data.reason);
      formData.append('message', data.message);

      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach((file) => {
          formData.append('attachments[]', file, file.name);
        });
      } else {
        formData.append('attachments', '');
      }

      const response = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/tenant/${tenantId}/requests`, formData, {
          observe: 'response',
        }),
      );

      return response.status === 200 || response.status === 201;
    } catch (error) {
      console.error('Error sending notification:', error);
      return false;
    }
  }

  public async getPhoneCommunications(tenantId: string | number): Promise<void> {
    if (this.phoneList().length > 0) return;

    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/tenant/${tenantId}/phone-communication`),
      );

      if (response && response.data) {
        const decodedList = response.data || [];
        this.phoneList.set(decodedList);
        this.toggleWhatsapp(decodedList.length > 0);
      }
    } catch (e) {
      console.error('Error fetching phones:', e);
    }
  }

  public async postPhoneCommunications(
    tenantId: string | number,
    phoneList: { id: number | null; phone: number }[],
  ): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<IResponse<{ id: number; phone: number }[]>>(
          `${environment.apiUrl}/tenant/${tenantId}/phone-communication`,
          phoneList,
          { observe: 'response' },
        ),
      );
      const decodedList = response.body?.data;
      if (decodedList) {
        this.phoneList.set(decodedList);
        this.toggleWhatsapp(decodedList.length > 0);
      }
      return response.status === 200 || response.status === 201;
    } catch (e) {
      console.error('Error sending phones:', e);

      return false;
    }
  }

  public toggleWhatsapp(value: boolean): void {
    this.receiveWhatsapp.set(value);
  }

  public clear(): void {
    this.tenant.set(null);
    this.tenantPostalInfo.set(null);
    this.contact.set(null);
    this.contactBrandUuid.set(null);
    this.settlementEmails.set([]);
    this.hasElectronicSettlement.set(false);
    this.hasMailboxDeliveryService.set(false);
    this.phoneList.set([]);
    this.receiveWhatsapp.set(false);
  }
}
